// Guide §3 "টাকা": Decimal(14,2), decimal.js, half-up rounding, round each line first then sum,
// Largest-Remainder allocation so proportional shares add up to the paisa. JavaScript Float is forbidden.
import Decimal from 'decimal.js';

Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_UP });

export { Decimal };
export type Money = Decimal;
export type MoneyInput = Decimal.Value;

export const ZERO = new Decimal(0);

/** Round to 2 decimals, half-up. */
export const money = (v: MoneyInput): Decimal => new Decimal(v).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
export const round2 = money;

/** Quantity helper: up to 3 decimals (kg / g / litre). Guide §3 "পরিমাণ". */
export const qty = (v: MoneyInput): Decimal => new Decimal(v).toDecimalPlaces(3, Decimal.ROUND_HALF_UP);

export const D = (v: MoneyInput): Decimal => new Decimal(v);

export function sum(values: Iterable<MoneyInput>): Decimal {
  let acc = new Decimal(0);
  for (const v of values) acc = acc.plus(v);
  return acc;
}

export const min = (a: MoneyInput, b: MoneyInput): Decimal => Decimal.min(a, b);
export const max = (a: MoneyInput, b: MoneyInput): Decimal => Decimal.max(a, b);

/**
 * Split `total` across `weights` so that the parts add up to *exactly* `total` (to the paisa).
 * Largest Remainder method (Guide §3).
 */
export function allocate(total: Decimal, weights: Decimal[]): Decimal[] {
  if (total.isNegative()) return allocate(total.negated(), weights).map((p) => p.negated());
  const sumW = weights.reduce((a, w) => a.plus(w), new Decimal(0));
  if (sumW.isZero()) return weights.map(() => new Decimal(0));
  const raw = weights.map((w) => total.times(w).dividedBy(sumW));
  const parts = raw.map((r) => r.toDecimalPlaces(2, Decimal.ROUND_DOWN));
  let paisa = total
    .minus(parts.reduce((a, p) => a.plus(p), new Decimal(0)))
    .times(100)
    .toDecimalPlaces(0, Decimal.ROUND_HALF_UP)
    .toNumber();
  const order = raw
    .map((r, i) => ({ i, frac: r.minus(parts[i]!) }))
    .sort((a, b) => b.frac.comparedTo(a.frac) || a.i - b.i);
  for (const { i } of order) {
    if (paisa <= 0) break;
    parts[i] = parts[i]!.plus('0.01');
    paisa--;
  }
  return parts;
}

/** Round to the nearest whole taka (used by the shop "rounding" setting). */
export const roundToTaka = (v: MoneyInput): Decimal => new Decimal(v).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);

/** Bill rounding (v10): to a whole ৳1, ৳5 or ৳10 — to the nearest step (half up) or always down (in the customer's favour). */
export const ROUNDING_STEPS = [1, 5, 10] as const;
export type RoundingStep = (typeof ROUNDING_STEPS)[number];
export type RoundingMode = 'nearest' | 'down';
export function roundToStep(v: MoneyInput, step: RoundingStep = 1, mode: RoundingMode = 'nearest'): Decimal {
  const x = new Decimal(v);
  if (step === 1 && mode === 'nearest') return roundToTaka(x); // the pre-v10 behaviour, bit for bit
  const q = x.dividedBy(step);
  const r = (mode === 'down' ? q.toDecimalPlaces(0, Decimal.ROUND_FLOOR) : q.toDecimalPlaces(0, Decimal.ROUND_HALF_UP)).times(step);
  // a small bill never rounds away to ৳0: fall back to the nearest whole taka
  if (x.gt(0) && r.lte(0)) return roundToTaka(x);
  return r;
}

/** Canonical decimal string with exactly 2 decimals — the wire format for money in JSON. */
export const moneyStr = (v: MoneyInput): string => money(v).toFixed(2);
export const qtyStr = (v: MoneyInput): string => qty(v).toFixed(3);

/** Percentage of an amount, rounded to paisa (e.g. discount %). */
export const percentOf = (amount: MoneyInput, pct: MoneyInput): Decimal =>
  money(new Decimal(amount).times(pct).dividedBy(100));

/** Safe parse of user-entered strings ("১২৩.৫০", "1,250", "৳ 50"). Returns null if not a valid non-negative-or-negative number. */
export function parseMoneyInput(input: string): Decimal | null {
  const ascii = bnToEnDigits(input).replace(/[,৳\s]/g, '').replace(/^Tk/i, '');
  if (!/^-?\d+(\.\d+)?$/.test(ascii)) return null;
  try {
    return new Decimal(ascii);
  } catch {
    return null;
  }
}

const BN_DIGITS = '০১২৩৪৫৬৭৮৯';
export const bnToEnDigits = (s: string): string => s.replace(/[০-৯]/g, (d) => String(BN_DIGITS.indexOf(d)));
export const enToBnDigits = (s: string): string => s.replace(/[0-9]/g, (d) => BN_DIGITS[Number(d)]!);

/** South-Asian digit grouping (12,34,567.50) as used in Bangladesh. */
export function groupBd(intStr: string): string {
  const neg = intStr.startsWith('-');
  const s = neg ? intStr.slice(1) : intStr;
  if (s.length <= 3) return intStr;
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return (neg ? '-' : '') + rest + ',' + last3;
}

export interface FormatTakaOptions {
  lang?: 'bn' | 'en';
  /** Always show 2 decimals (default: only when there are paisa) */
  fixed?: boolean;
  symbol?: boolean;
}

/** "৳১২,৫০০" (bn) or "৳12,500" (en). Guide/Bible §2.9: money shown with 2 decimals max. */
export function formatTaka(v: MoneyInput, opts: FormatTakaOptions = {}): string {
  const { lang = 'bn', fixed = false, symbol = true } = opts;
  const d = money(v);
  const neg = d.isNegative() && !d.isZero();
  const abs = d.abs();
  const hasPaisa = !abs.isInteger();
  const [i, f] = (fixed || hasPaisa ? abs.toFixed(2) : abs.toFixed(0)).split('.');
  let out = groupBd(i!) + (f ? '.' + f : '');
  if (lang === 'bn') out = enToBnDigits(out);
  return `${neg ? '-' : ''}${symbol ? '৳' : ''}${out}`;
}

export function formatQty(v: MoneyInput, lang: 'bn' | 'en' = 'bn'): string {
  const s = new Decimal(v).toDecimalPlaces(3).toString();
  return lang === 'bn' ? enToBnDigits(s) : s;
}

export function formatPercent(v: MoneyInput, lang: 'bn' | 'en' = 'bn'): string {
  const s = new Decimal(v).toDecimalPlaces(1).toFixed(1) + '%';
  return lang === 'bn' ? enToBnDigits(s) : s;
}

export const formatInt = (n: number, lang: 'bn' | 'en' = 'bn'): string => {
  const s = groupBd(String(Math.trunc(n)));
  return lang === 'bn' ? enToBnDigits(s) : s;
};
